import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { FileText, LogOut, LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";

export function Header() {
  const { t, lang, setLang } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const authed = user && typeof user === "object";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/10 text-[hsl(var(--gold))]">
            <FileText className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))]/80">
              {t.tagline}
            </div>
            <div className="font-display text-base font-bold tracking-tight">
              {t.brand}
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <NavItem to="/" label={t.nav.home} testid="nav-home" />
          <NavItem to="/advisor" label={t.nav.advisor} testid="nav-advisor" />
          <NavItem to="/pricing" label={t.nav.pricing} testid="nav-pricing" />
          {authed && (
            <>
              <NavItem to="/dashboard" label={t.nav.dashboard} testid="nav-dashboard" />
              <NavItem to="/wizard" label={t.nav.new_form} testid="nav-new-form" />
              {user?.license?.license_type !== "particular" && (
                <NavItem to="/packages" label={t.packages.nav} testid="nav-packages" />
              )}
              {user?.role === "admin" && (
                <NavItem to="/admin" label={t.nav.admin} testid="nav-admin" />
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hairline flex overflow-hidden rounded-md text-xs font-medium" role="group" aria-label="Language switch">
            <button
              data-testid="lang-switch-pt"
              onClick={() => setLang("pt")}
              className={`px-3 py-1.5 font-mono uppercase tracking-widest transition-colors ${
                lang === "pt"
                  ? "bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]"
                  : "bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              PT
            </button>
            <button
              data-testid="lang-switch-en"
              onClick={() => setLang("en")}
              className={`border-l border-border px-3 py-1.5 font-mono uppercase tracking-widest transition-colors ${
                lang === "en"
                  ? "bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]"
                  : "bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-switch-es"
              onClick={() => setLang("es")}
              className={`border-l border-border px-3 py-1.5 font-mono uppercase tracking-widest transition-colors ${
                lang === "es"
                  ? "bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]"
                  : "bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              ES
            </button>
          </div>

          {authed ? (
            <>
              <span
                data-testid="header-user-email"
                className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:inline"
              >
                {user.email}
              </span>
              <Button
                variant="outline"
                onClick={handleLogout}
                data-testid="header-logout-button"
                className="rounded-none"
              >
                <LogOut className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">{t.common.logout}</span>
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link" className="hidden sm:block">
                <Button variant="outline" className="rounded-md border-border bg-transparent">
                  <LogIn className="mr-2 h-4 w-4" />
                  {t.auth.login_cta}
                </Button>
              </Link>
              <Link to="/register" data-testid="header-register-link">
                <Button className="rounded-md bg-[hsl(var(--gold))] font-semibold text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-soft))]">
                  <UserPlus className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">{t.auth.register_cta}</span>
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function NavItem({ to, label, testid }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testid}
      className={({ isActive }) =>
        `px-3 py-2 text-sm font-medium transition-colors ${
          isActive
            ? "text-[hsl(var(--gold))]"
            : "text-foreground/70 hover:text-foreground"
        }`
      }
    >
      {label}
    </NavLink>
  );
}
